import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { addCurrentThemeClassToEle } from "@/utils";
import { getDashboardDetailsApiCall, getSeriesBySubscriptionGroupApiCall } from "@/services/api";
import { setDashboardDetails } from "@/store/slices/dashboard/dashboardDetailsSlice";
import {
  handleUpdateUserPurchaseContentApiCall,
  handleUpdateWelcomeSeriesContentApiCall,
} from "@/utils/apiUtils";
import VeryTopNavbar from "@/components/common/veryTopNavbar";
import FloatingSupportButtons from "@/components/common/floatingSupportButtons";
import { ArrowLeft, ChevronLeft } from "lucide-react";
import { seriesStatus } from "@/pages/dashboard/constants";
import SeriesCard from "@/components/dashboard/seriesCard";
import "./style.css";
import { useSafeAreaInsets } from "@/hooks/useSafeAreaInsets";
const SGROUP_UNAVAILABLE_THUMB =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='120'%3E%3Crect fill='%23e5e7eb' width='160' height='120' rx='8'/%3E%3C/svg%3E";

function unavailableSeriePlaceholder(seriesId: string) {
  return {
    _id: `sgroup-unavailable-${seriesId}`,
    seriesId,
    title: "Series unavailable",
    thumbnailUrl: SGROUP_UNAVAILABLE_THUMB,
    seriesStatus: seriesStatus.enrolled,
    progress: 0,
  };
}

type SubscriptionGroupSeriesRow = {
  seriesId: string;
  serie: any;
};

export default function SubscriptionGroupRoute() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { topSafeComponent, bottomSafeComponent } = useSafeAreaInsets();
  const dashboardFetchAttempted = useRef(false);
  const { id } = useParams();
  const currentTheme = useSelector((state: RootState) => state.currentThemeReducer.currentTheme);
  const dashboardDetails = useSelector((state: RootState) => state.dashboardDetailsReducer.dashboardDetails);
  const anyOneInstallmentOverDue = useMemo(() => {
    const list = dashboardDetails?.courses;
    if (!Array.isArray(list)) return false;
    return list.some((c: any) => Boolean(c?.installmentOverdue));
  }, [dashboardDetails?.courses]);

  const subscriptionGroups = dashboardDetails?.subscriptionGroups ?? [];
  const allSeries = dashboardDetails?.series ?? [];

  const group = useMemo(() => {
    if (!id) return undefined;
    return subscriptionGroups.find((g: any) => g._id === id);
  }, [id, subscriptionGroups]);

  /** Series from GET `/subscriptions/series-by-subscription-group/:id`; `undefined` = still loading. */
  const [apiSeriesList, setApiSeriesList] = useState<any[] | undefined>(undefined);

  useEffect(() => {
    if (!id) {
      setApiSeriesList([]);
      return;
    }
    let cancelled = false;
    setApiSeriesList(undefined);

    (async () => {
      const res = await getSeriesBySubscriptionGroupApiCall(id);
      if (cancelled) return;
      if (!res?.status) {
        setApiSeriesList([]);
        return;
      }
      const d = res.data;
      const list = Array.isArray(d) ? d : Array.isArray(d?.series) ? d.series : [];
      setApiSeriesList(list);
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const seriesRows = useMemo((): SubscriptionGroupSeriesRow[] => {
    if (apiSeriesList === undefined) return [];

    if (apiSeriesList.length > 0) {
      return apiSeriesList.map((serie: any) => ({
        seriesId: String(serie.seriesId ?? serie._id ?? ""),
        serie,
      }));
    }

    if (group?.seriesIds?.length) {
      return group.seriesIds.map((seriesId: string) => {
        const serie = allSeries.find((s: any) => s.seriesId === seriesId || s._id === seriesId);
        return { seriesId, serie };
      });
    }

    return [];
  }, [apiSeriesList, group, allSeries]);

  const seriesListLoading = Boolean(id) && apiSeriesList === undefined;

  useEffect(() => {
    if (
      dashboardDetails != null &&
      Object.prototype.hasOwnProperty.call(dashboardDetails, "subscriptionGroups")
    ) {
      return;
    }
    if (dashboardFetchAttempted.current) return;
    dashboardFetchAttempted.current = true;

    const fetchDashboard = async () => {
      const payload = { purchaseId: null };
      const ok1 = await handleUpdateUserPurchaseContentApiCall(payload);
      const ok2 = await handleUpdateWelcomeSeriesContentApiCall(payload);
      if (!ok1 || !ok2) return;

      const res = await getDashboardDetailsApiCall();
      if (res?.status && res?.data) {
        dispatch(setDashboardDetails(res.data));
      }
    };

    fetchDashboard();
  }, [dispatch, dashboardDetails]);

  const onBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  const groupTitle = group?.title ?? "Subscription group";

  return (
    <div className={`sgroupPage ${addCurrentThemeClassToEle(currentTheme, "sgroupPage")}`}>
      {/* <VeryTopNavbar showRhs={true} /> */}
    {topSafeComponent}
      <header className={`sgroupHeaderStrip mt-[45px] min-[1280px]:mt-[0px] ${addCurrentThemeClassToEle(currentTheme, 'miniNav')}`}>
        <div className="sgroupColumn">
          <div className="sgroupTopRow">
            <button type="button" onClick={onBack} aria-label="Back" className="sgroupHeaderBackBtn ms-[-12px] min-[1024px]:ms-[0px]">
              <ArrowLeft
                className="sgroupHeaderBackBtnIcon sgroupHeaderBackBtnIcon--desktop"
                size={20}
                strokeWidth={2}
                aria-hidden
              />
              <ChevronLeft
                className="sgroupHeaderBackBtnIcon sgroupHeaderBackBtnIcon--mobile"
                size={20}
                strokeWidth={2}
                aria-hidden
              />
            </button>
            <h1 className="sgroupTopTitle">{groupTitle}</h1>
          </div>
        </div>
      </header>

      <div className="sgroupMain">
        <div className="sgroupColumn sgroupContent">
          <div className="sgroupPanel pb-[180px] min-[1280px]:pb-0">
            {seriesListLoading ? (
              <p className="sgroupLoadingText">Loading…</p>
            ) : null}
            <div className="sgroupGrid" role="list">
              {seriesRows.map(({ seriesId, serie }) => {
                if (!serie) {
                  return (
                    <div key={seriesId} className="sgroupSeriesCardCell" role="listitem">
                      <SeriesCard
                        serie={unavailableSeriePlaceholder(seriesId)}
                        anyOneInstallmentOverDue={anyOneInstallmentOverDue}
                        source="dashboard"
                        hideThumbnail
                        subscriptionGroupCard
                      />
                    </div>
                  );
                }

                return (
                  <div key={serie._id ?? seriesId} className="sgroupSeriesCardCell" role="listitem">
                    <SeriesCard
                      serie={serie}
                      anyOneInstallmentOverDue={anyOneInstallmentOverDue}
                      source="dashboard"
                      fromSubscription
                      hideThumbnail
                      subscriptionGroupCard
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <FloatingSupportButtons />
    </div>
  );
}
